import React from 'react';

import clsx from 'clsx';

import { useBrand } from '@atc/brand';

import {
    Col,
    DisclaimerText, Image, Link, List, SubHeading,
} from 'reaxl';
import { DelayedImpressionObserver, sendClick, sendImpressions } from 'reaxl-analytics';
import { psxOptionAddOnProtectionsClick, psxOptionAddOnProtectionsImpression, psxVDPVehicleProtectionGAPProtectionClick, psxVDPVehicleProtectionVehicleServiceContractsClick } from 'reaxl-analytics-handlers';

import Card from './Card';
import { cardHeaders, cardImages } from './staticContent';

const psxAdditionalProtection = {
    psxVehicleServiceContracts: {
        subHeader: 'Vehicle Service Contracts',
        description: 'PSX offers extended protection for eligible vehicles in the event of unexpected costly repairs.',
        items: [
            'Customize your coverage',
            'Terms from 3 months to 7 years available',
            'Roadside Assistance',
            'Towing',
            'Rental Cars',
        ],
        link: {
            href: '/marketplace/vehicle-service-contracts',
            linkText: 'Learn More about Vehicle Service Contracts',
        },
        image: {
            src: '/cm-api/content/static/img/psx/vpp/asc_logo.png',
            width: '82',
        },
        poweredBy: 'Powered by Auto Services Company, Inc.™',
        border: false,
        analyticsHandler: psxVDPVehicleProtectionVehicleServiceContractsClick,
    },
    psxGAPProtection: {
        subHeader: 'GAP Protection',
        description: 'PSX offers GAP Protection if you are sensitive to sudden cash expenses, are buying a car that devalues quickly, or have a small down payment.',
        items: [
            'Available for PSX financing* up to 84 months',
            'Covers most or all of a remaining net outstanding balance including deductible up to $1000 (where permitted by state law)',
        ],
        link: {
            href: '/marketplace/gap-insurance',
            linkText: 'Learn More about GAP Protection',
        },
        image: {
            src: '/cm-api/content/static/img/psx/vpp/pac_logo.png',
            width: '124',
        },
        poweredBy: 'Powered by Partners™ Alliance Corporation',
        disclaimer: '*In some areas, financing may not be an opton and only cash payments will be accepted.',
        border: true,
        analyticsHandler: psxVDPVehicleProtectionGAPProtectionClick,
    },
};

function PsxAdditionalProtectionCard({
    listingId,
    filterValues,
    ...rest
}) {
    const { brands, isBrand } = useBrand();
    const isAtc = isBrand(brands.ATC_BRAND);

    const handleCardExpansion = (event) => {
        sendClick(psxOptionAddOnProtectionsClick, event, { listingId });
    };

    const handleImpression = () => {
        sendImpressions({ name: psxOptionAddOnProtectionsImpression, data: { listingId } });
    };

    return (
        <DelayedImpressionObserver
            onChange={handleImpression}
            key={`psx-optional-protection-card-${listingId}-delayed-impression-observer`}
        >
            <Card
                id="psx_protections"
                heading={cardHeaders.ADDITIONAL_PROTECTIONS}
                image={isAtc ? cardImages.PROTECTION_ATC : cardImages.PROTECTION}
                summary="Vehicle service contracts and GAP insurance available."
                className="clearfix"
                {...rest}
                showTile
                collapsible
                onExpand={handleCardExpansion}
                isInverse
            >
                <div className="display-flex-md margin-bottom-4 margin-bottom-sm-0 margin-top-5">
                    {Object.keys(psxAdditionalProtection).map((item, index) => {
                        const { description, items, link: { href, linkText }, disclaimer, image, subHeader, poweredBy, analyticsHandler } = psxAdditionalProtection[item];

                        const handleClick = (event) => {
                            if (subHeader === 'Vehicle Service Contracts') {
                                sendClick(analyticsHandler, event, { listingId, filterValues });
                            } else {
                                sendClick(analyticsHandler, event, { listingId });
                            }
                        };

                        return (
                            <Col
                                key={subHeader}
                                md={6}
                                className={clsx('padding-0', {
                                    'border-gray-muted border-top border-top-md-0 border-left-0 border-left-md border-bottom-0 border-right-0': index,
                                    'padding-top-4 padding-top-md-0 padding-left-md-4': index,
                                    'padding-right-md-4': !index,
                                })}
                            >
                                <Image
                                    height={40}
                                    aria-hidden="true"
                                    alt=""
                                    {...image}
                                />
                                <SubHeading
                                    size={300}
                                    className="margin-vertical-4"
                                >
                                    {subHeader}
                                </SubHeading>
                                <p>{description}</p>
                                <List items={items} />
                                <Link
                                    href={href}
                                    className="display-block margin-bottom-4"
                                    target="_blank"
                                    rel="nofollow noopener"
                                    onClick={handleClick}
                                    inverse
                                >
                                    {linkText}
                                </Link>
                                <DisclaimerText
                                    withContrast
                                    componentClass="p"
                                    inverse
                                >
                                    {poweredBy}
                                </DisclaimerText>
                                {disclaimer && (
                                    <DisclaimerText
                                        withContrast
                                        inverse
                                    >
                                        {disclaimer}
                                    </DisclaimerText>
                                )}
                            </Col>
                        );
                    })}
                </div>
            </Card>
        </DelayedImpressionObserver>
    );
}

export default PsxAdditionalProtectionCard;
